import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock, PlayCircle, CheckCircle2, LogOut, ArrowRight, Calendar } from "lucide-react";
import { authHeader, clearUserToken, getUserToken } from "@/utils/userAuth";
import ConsultationDialog from "@/components/ConsultationDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MembersAreaPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consultOpen, setConsultOpen] = useState(false);
  const [consultDone, setConsultDone] = useState(false);

  useEffect(() => {
    if (!getUserToken()) {
      navigate("/?login=1");
      return;
    }
    (async () => {
      try {
        const [me, course] = await Promise.all([
          axios.get(`${API}/users/me`, { headers: authHeader() }),
          axios.get(`${API}/me/course`, { headers: authHeader() }),
        ]);
        setUser(me.data);
        setData(course.data);
      } catch {
        clearUserToken();
        navigate("/?login=1");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = () => { clearUserToken(); navigate("/"); };

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--np-tertiary)" }}>
        <Loader2 className="animate-spin" size={26} style={{ color: "var(--np-secondary)" }} />
      </div>
    );
  }

  const firstName = user?.name?.split(" ")[0] || "studente";

  return (
    <main className="min-h-screen pb-32" style={{ background: "var(--np-tertiary)" }} data-testid="members-area">
      {/* Header (web app shell) */}
      <header className="border-b" style={{ background: "var(--np-quaternary)", borderColor: "rgba(26,42,58,0.06)" }}>
        <div className="mx-auto px-6 md:px-10 py-2.5 flex items-center justify-between" style={{ maxWidth: 1280 }}>
          <Link to="/" aria-label="Neuroscienza Pratica" className="inline-flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/ypgcjh4z_11%20%2829%29.png"
              alt="Neuroscienza Pratica"
              className="block"
              style={{ width: "clamp(220px, 24vw, 380px)", aspectRatio: "4 / 1", objectFit: "cover", objectPosition: "center" }}
            />
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm" style={{ color: "var(--np-muted)" }}>
              Ciao, <strong style={{ color: "var(--np-primary)" }}>{firstName}</strong>
            </span>
            <button data-testid="logout-btn" onClick={handleLogout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border transition hover:bg-white" style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}>
              <LogOut size={13} /> Esci
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto px-6 pt-14 md:pt-20 pb-8" style={{ maxWidth: 1100 }}>
        <p className="text-[12px] tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: "var(--np-secondary)" }}>
          <Link to="/corso" className="hover:underline">← Tutti i corsi</Link> · La tua area corso
        </p>
        <h1 className="text-3xl md:text-5xl leading-[1.05] tracking-tight" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
          {data.title}
        </h1>
        <p className="mt-4 text-base md:text-lg leading-relaxed max-w-3xl" style={{ color: "var(--np-muted)" }}>
          {data.subtitle}
        </p>
        <ProgressBar unlocked={data.unlocked_lessons} total={data.total_lessons} />
      </section>

      {/* Lessons grid */}
      <section className="mx-auto px-6 pt-6" style={{ maxWidth: 1100 }}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.lessons.map((L) => (
            <LessonCard key={L.id} lesson={L} hasFull={data.has_full_access} />
          ))}
        </div>
      </section>

      {/* Contact-request banner (replaces the €197 self-checkout per business decision) */}
      {!data.has_full_access && (
        <section className="mx-auto px-6 pt-14" style={{ maxWidth: 1100 }}>
          <div className="rounded-2xl p-7 md:p-10" style={{ background: "var(--np-quaternary)" }} data-testid="contact-request-banner">
            <p className="text-[12px] tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: "var(--np-secondary)" }}>
              Hai sbloccato {data.unlocked_lessons} di 9 lezioni
            </p>
            <h2 className="text-2xl md:text-3xl leading-tight" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
              Vuoi ricevere le restanti lezioni?
            </h2>
            <p className="mt-3 text-base leading-relaxed max-w-2xl" style={{ color: "var(--np-muted)" }}>
              Le prossime lezioni completano il protocollo. Per accedere e ricevere supporto personalizzato, richiedi di essere contattato dal team di Alessandro Fulgheri.
            </p>
            <ContactRequestButton />
          </div>
        </section>
      )}

      {/* Consultation packages CTA — sempre disponibile */}
      <section className="mx-auto px-6 pt-8 pb-2" style={{ maxWidth: 1100 }}>
        <div data-testid="consultation-cta-banner" className="rounded-2xl border-2 p-7 md:p-9 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
          style={{ borderColor: "var(--np-secondary)", background: "linear-gradient(135deg, #fff8f4 0%, #ffffff 70%)" }}>
          <div className="flex-1">
            <p className="text-[12px] tracking-[0.22em] uppercase font-semibold mb-2" style={{ color: "var(--np-secondary)" }}>
              Consulenza 1-to-1
            </p>
            <h2 className="text-2xl md:text-[28px] leading-tight" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
              Vuoi un confronto diretto sul tuo caso?
            </h2>
            <p className="mt-2 text-[15px] leading-relaxed max-w-2xl" style={{ color: "var(--np-muted)" }}>
              Scegli tra <strong>3 pacchetti</strong> pensati per obiettivi diversi: dal chiarimento rapido di 30 minuti all&apos;affiancamento di 4 settimane. Il team di Alessandro Fulgheri ti contatta entro 24 ore.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setConsultOpen(true)}
            data-testid="open-consultation-dialog"
            disabled={consultDone || user?.consultation_booked}
            className="cta-btn whitespace-nowrap disabled:opacity-60">
            <Calendar size={16} /> {consultDone || user?.consultation_booked ? "Richiesta inviata" : "Vedi i 3 pacchetti"}
          </button>
        </div>
      </section>

      <ConsultationDialog
        open={consultOpen}
        user={user}
        onClose={() => setConsultOpen(false)}
        onSubmitted={() => { setConsultDone(true); setConsultOpen(false); }}
      />
    </main>
  );
}

function ContactRequestButton() {
  const [state, setState] = React.useState("idle");
  const submit = async () => {
    setState("loading");
    try {
      await axios.post(`${API}/leads/contact-request`, { topic: "full_course_info" }, { headers: authHeader() });
      setState("done");
    } catch {
      setState("error");
    }
  };
  if (state === "done") {
    return (
      <p className="mt-7 inline-flex items-center gap-2 text-sm font-semibold" data-testid="contact-request-confirmation" style={{ color: "var(--np-secondary)" }}>
        <CheckCircle2 size={18} /> Richiesta inviata! Il team ti contatterà a breve.
      </p>
    );
  }
  return (
    <button
      type="button"
      onClick={submit}
      disabled={state === "loading"}
      data-testid="cta-contact-request"
      className="cta-btn mt-7 inline-flex disabled:opacity-60"
    >
      {state === "loading" ? (<><Loader2 className="animate-spin" size={17} /> Sto inviando…</>)
        : (<>Richiedi di essere contattato <ArrowRight size={18} strokeWidth={2.4} /></>)}
    </button>
  );
}

function ProgressBar({ unlocked, total }) {
  const pct = Math.round((unlocked / total) * 100);
  return (
    <div className="mt-7 max-w-xl" data-testid="progress-bar">
      <div className="flex items-center justify-between mb-2 text-[12px] font-semibold uppercase tracking-wider" style={{ color: "var(--np-muted)" }}>
        <span>Progresso</span>
        <span>{unlocked} / {total} lezioni</span>
      </div>
      <div className="h-2 w-full rounded-full" style={{ background: "rgba(26,42,58,0.08)" }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "var(--np-secondary)" }} />
      </div>
    </div>
  );
}

function LessonCard({ lesson, hasFull }) {
  const locked = !lesson.unlocked;
  const href = `/corso/lezione/${lesson.n}`;
  return (
    <Link
      to={locked ? "#" : href}
      onClick={(e) => locked && e.preventDefault()}
      data-testid={`lesson-card-${lesson.n}`}
      className={`block rounded-2xl border bg-white p-5 transition ${locked ? "opacity-70 cursor-not-allowed" : "hover:shadow-md hover:-translate-y-0.5"}`}
      style={{ borderColor: locked ? "#e5ecf2" : "rgba(217,94,57,0.18)" }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold" style={{ background: locked ? "rgba(26,42,58,0.06)" : "var(--np-quaternary)", color: locked ? "var(--np-muted)" : "var(--np-secondary)" }}>
          {lesson.n}
        </span>
        {locked ? (
          <Lock size={18} style={{ color: "var(--np-muted)" }} />
        ) : (
          <PlayCircle size={22} style={{ color: "var(--np-secondary)" }} />
        )}
      </div>
      <p className="text-[11px] tracking-[0.18em] uppercase font-semibold mb-1.5" style={{ color: locked ? "var(--np-muted)" : "var(--np-secondary)" }}>
        {locked ? "BLOCCATA" : "DISPONIBILE"}
      </p>
      <h3 className="text-base md:text-lg leading-snug font-semibold" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
        {lesson.title}
      </h3>
      {!locked && (
        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--np-secondary)" }}>
          Guarda ora <ArrowRight size={14} strokeWidth={2.5} />
        </div>
      )}
      {locked && lesson.n >= 4 && !hasFull && (
        <p className="mt-4 text-[12px]" style={{ color: "var(--np-muted)" }}>
          Sblocca questa lezione con il corso completo
        </p>
      )}
    </Link>
  );
}
